import React, { ReactNode } from "react";
import styled from "styled-components";
import { ColorType, getFontColor } from "../../../configs/constants/ColorType";
import { getFontSize, TextSizeType } from "../../../utils/theme.utils";
import HelpTooltip from "../../Shared/HelpTooltip/HelpTooltip";
import KnowledgeBaseTrigger from "../../Shared/KnowledgeBase/KnowledgeBaseTrigger";

// TODO move color , align types globally so that other components can share them.
type TextWeightType = "light" | "regular" | "medium" | "bold" | undefined;
type TextAlignType = "left" | "right" | "center" | "justify" | undefined;
type TextDecorationType = "underline" | "overline" | "line-through" | undefined;
type TextTransformType = "uppercase" | "capitalize" | "lowercase" | undefined;

export interface AppTextProps extends AppTextStyleProps {
	children: ReactNode;
	as?: any;
	href?: string;
}

export interface AppTextStyleProps {
	size?: TextSizeType;
	sizeMobile?: TextSizeType;
	weight?: TextWeightType;
	color?: ColorType;
	textAlign?: TextAlignType;
	margin?: string;
	textDecoration?: TextDecorationType;
	textTransform?: TextTransformType;
	lineHeight?: string | number;
	lineClamp?: number;
	letterSpacing?: string | number;
	className?: string;
	onClick?: () => void;
	cursor?: "default" | "pointer";
	italic?: boolean;
}

const AppText: React.FC<AppTextProps> = ({ children, as, href, ...props }) => {
	return (
		<AppTextStyles {...props} as={as} href={href}>
			{children}
		</AppTextStyles>
	);
};

export default AppText;

const AppTextStyles = styled.p<AppTextStyleProps>`
	margin: ${(props) => (props.margin ? props.margin : 0)};
	padding: 0;
	text-align: ${(props) => (props.textAlign ? props.textAlign : "left")};
	color: ${(props) =>
		props.color ? getFontColor(props.color, props.theme) : props.theme.colors.COLOR_DARK};

	line-height: ${(props) => props.lineHeight || "unset"};

	${(props) => (props.cursor ? "cursor: pointer;" : "")};

	font-style: ${(props) => (props.italic ? "italic" : "normal")};

	font-weight: ${(props) => {
		switch (props.weight) {
			case "regular":
				return props.theme.fontWeight.FONT_WEIGHT_REGULAR;
			case "medium":
				return props.theme.fontWeight.FONT_WEIGHT_MEDIUM;
			case "bold":
				return props.theme.fontWeight.FONT_WEIGHT_BOLD;
			default:
				return props.theme.fontWeight.FONT_WEIGHT_REGULAR;
		}
	}};

	font-size: ${(props) => getFontSize(props.size)};

	${(props) => {
		if (props.sizeMobile) {
			return `
				@media screen and (max-width: 800px) {
					font-size: ${getFontSize(props.sizeMobile)}
				}
				`;
		}
	}}
	text-decoration: ${(props) => {
		switch (props.textDecoration) {
			case "underline":
				return "underline";
			case "overline":
				return "overline";
			case "line-through":
				return "line-through";
			default:
		}
	}};

	text-transform: ${(props) => {
		switch (props.textTransform) {
			case "capitalize":
				return "capitalize";
			case "lowercase":
				return "lowercase";
			case "uppercase":
				return "uppercase";
		}
	}};

	${(props) => {
		if (props.lineClamp) {
			return `
				display: -webkit-box;
				-webkit-line-clamp: ${props.lineClamp};
				-webkit-box-orient: vertical;
				overflow: hidden;
				text-overflow: ellipsis;
				word-break: break-all;	
			`;
		}
	}};

	letter-spacing: ${(props) => props.letterSpacing && props.letterSpacing};
`;

/**
 * Preset: Form header
 */
export const FormHeader: React.FC<AppTextStyleProps & { children: React.ReactNode }> = ({
	children,
	margin = "1.5rem 0",
	...props
}) => (
	<AppText margin={margin} color="grayText" size="xm" weight="bold" {...props}>
		{children}
	</AppText>
);

/**
 * Preset: Form label
 */
type FormLabelBaseProps = AppTextStyleProps & {
	children: React.ReactNode;
	knowledgeBaseTooltip?: string;
};

type FormLabelWithTooltip = FormLabelBaseProps & {
	tooltip: string | React.ReactNode;
	knowledgeBaseTag?: never;
	showKnowledgeBaseForTagOnHover?: never;
};

type FormLabelWithKnowledgeBase = FormLabelBaseProps & {
	tooltip?: never;
	knowledgeBaseTag: string;
	showKnowledgeBaseForTagOnHover?: never;
};

type FormLabelWithHoverKnowledgeBase = FormLabelBaseProps & {
	tooltip?: never;
	knowledgeBaseTag?: never;
	showKnowledgeBaseForTagOnHover: string;
};

type FormLabelNoHelp = FormLabelBaseProps & {
	tooltip?: never;
	knowledgeBaseTag?: never;
	showKnowledgeBaseForTagOnHover?: never;
};

type FormLabelProps =
	| FormLabelWithTooltip
	| FormLabelWithKnowledgeBase
	| FormLabelWithHoverKnowledgeBase
	| FormLabelNoHelp;

export const FormLabel: React.FC<FormLabelProps> = ({
	children,
	margin = "0 0 0.4rem 0",
	tooltip,
	knowledgeBaseTag,
	knowledgeBaseTooltip = "Läs mer i hjälpcentret",
	showKnowledgeBaseForTagOnHover,
	...props
}) => {
	const [isHovered, setIsHovered] = React.useState(false);

	// Development-only validation
	if (process.env.NODE_ENV === "development") {
		const helpPropsCount = [tooltip, knowledgeBaseTag, showKnowledgeBaseForTagOnHover].filter(
			Boolean
		).length;

		if (helpPropsCount > 1) {
			console.error(
				"FormLabel: Cannot use multiple help props. " +
					"Use only one of: 'tooltip', 'knowledgeBaseTag', or 'showKnowledgeBaseForTagOnHover'."
			);
		}
	}

	return (
		<StyledFormLabel
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* 14px / 500 / #333 — the same scale as CheckboxNew's label and the
			    shadcn primitives, so the field value stays the heaviest thing in a group. */}
			<AppText color="dark" size="s" weight="medium" margin={margin} {...props}>
				{children}
			</AppText>
			{knowledgeBaseTag ? (
				<KnowledgeBaseTrigger
					tag={knowledgeBaseTag}
					tooltip={knowledgeBaseTooltip}
					margin={margin}
				/>
			) : showKnowledgeBaseForTagOnHover ? (
				<KnowledgeBaseTriggerWrapper isVisible={isHovered}>
					<KnowledgeBaseTrigger
						tag={showKnowledgeBaseForTagOnHover}
						tooltip={knowledgeBaseTooltip}
						margin={margin}
					/>
				</KnowledgeBaseTriggerWrapper>
			) : tooltip ? (
				<HelpTooltip margin={margin}>{tooltip}</HelpTooltip>
			) : null}
		</StyledFormLabel>
	);
};

const StyledFormLabel = styled.div`
	display: flex;
	align-items: center;
`;

const KnowledgeBaseTriggerWrapper = styled.span<{ isVisible: boolean }>`
	opacity: ${(props) => (props.isVisible ? 1 : 0)};
	visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
	transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
	display: inline-flex;
	align-items: center;
`;

/**
 * Preset: LinkText
 */
export const LinkText: React.FC<
	AppTextStyleProps & { href?: string; target?: string; as?: any; children: React.ReactNode }
> = ({ children, href, target, as = "a", ...props }) => (
	<AppText
		color="actionBlue"
		textDecoration="underline"
		cursor="pointer"
		{...props}
		href={href}
		as={as}
		// @ts-ignore
		target={target}
	>
		{children}
	</AppText>
);
